import { channelDisplayName } from "@redotech/redo-merchant-support-app/utils";
import type { TicketChannelCondition as TicketChannelConditionType } from "@redotech/redo-model/advanced-flow/rules/schemas";
import { ConversationPlatform } from "@redotech/redo-model/conversation";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";

const channels = Object.values(ConversationPlatform);

const channelItems: RedoListItem<ConversationPlatform>[] = channels.map(
  (channel) => ({
    id: channel,
    type: "text" as const,
    text: channelDisplayName(channel),
    value: channel,
  }),
);

export interface TicketChannelConditionProps {
  condition: TicketChannelConditionType;
  onChange: (updated: TicketChannelConditionType) => void;
}

export const TicketChannelCondition = memo(function TicketChannelCondition({
  condition,
  onChange,
}: {
  condition: TicketChannelConditionType;
  onChange: (updated: TicketChannelConditionType) => void;
}) {
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selectedItems: RedoListItem<ConversationPlatform>[] = useMemo(() => {
    return (condition.channels || []).map((channel) => ({
      id: channel,
      type: "text" as const,
      text: channelDisplayName(channel),
      value: channel,
    }));
  }, [condition.channels]);

  const displayItem: RedoListItem<ConversationPlatform> | undefined =
    useMemo(() => {
      if (selectedItems.length === 0) {
        return undefined;
      }
      if (selectedItems.length === 1) {
        return selectedItems[0];
      }
      return {
        id: "display",
        type: "text" as const,
        text: `${selectedItems.length} channels selected`,
        value: ConversationPlatform.FACEBOOK,
      };
    }, [selectedItems]);

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xs" textColor="tertiary">
        Channel
      </Text>
      <RedoSingleSelectDropdownInputButton
        isDropdownOpen={dropdownOpen}
        onClick={() => setDropdownOpen(!dropdownOpen)}
        placeholder="Select channels"
        ref={setDropdownButtonRef}
        selectedItem={displayItem}
        size="xs"
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={channelItems}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          onChange({ ...condition, channels: items.map((item) => item.value) });
        }}
        size="xs"
      />
    </Flex>
  );
});
